import MainModel from "../../../components/Models/MainModel";
import Button from "../../../components/buttons/Button";
import SecondaryButton from "../../../components/buttons/SecondaryButton";
import Image from "../../../components/images/Image";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";

/**
 * Confirmation shown before deleting a bundle plan. Deleting renumbers every USSD
 * plan code for the MNO, so the prompt says so explicitly.
 */
export function ConfirmDeletePlanModal({
  loading,
  onCancel,
  onConfirm,
}: {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { translate } = useTranslation(adminContents);

  return (
    <MainModel
      modelWidth="w-full max-w-[470px]"
      // Dismissing mid request would leave the delete running with no feedback.
      disableOutsideClick={loading}
      handleClose={onCancel}
    >
      <div data-testid="confirm-delete-modal">
        <div className="flex justify-center">
          <Image src="alert-circle" objectFit="object-contain" testId="alert-icon" />
        </div>
        <h1 className="text-primary_text text-heading3 text-center pt-6 pb-4 border-b border-[#E5E9EB]">
          {translate("Do you want to Delete ?")}
        </h1>
        <p className="text-secondary_text text-body1 pt-4">
          {translate(
            "Once a bundle plan is deleted, all USSD Plan Codes will be reordered accordingly."
          )}
        </p>
        <div className="flex justify-end gap-3 mt-8">
          <SecondaryButton
            buttonName={translate("Cancel")}
            handleClick={onCancel}
            buttonDisabled={loading}
            className="w-[92px] h-[42px] font-semibold text-small"
            testId="cancel-delete"
          />
          <Button
            buttonName={translate("Delete")}
            buttonOnclick={onConfirm}
            loading={loading}
            buttonWidth="w-[92px]"
            buttonHight="h-[42px]"
            bgColor="bg-negative"
            textSize="text-small"
            testId="confirm-delete"
          />
        </div>
      </div>
    </MainModel>
  );
}

/**
 * Confirmation shown once a bundle plan has been deleted. `resequencedCount` is the
 * number of remaining plans whose USSD plan code shifted down; when it is zero
 * nothing was renumbered, so the reordering sentence is dropped.
 */
export function DeleteSuccessModal({
  resequencedCount = 0,
  onGoBack,
}: {
  resequencedCount?: number;
  onGoBack: () => void;
}) {
  const { translate } = useTranslation(adminContents);

  return (
    <MainModel modelWidth="w-full max-w-[470px]" disableOutsideClick handleClose={onGoBack}>
      <div data-testid="delete-success-modal">
        <div className="flex justify-center">
          <Image
            src="delete_red"
            objectFit="object-contain"
            className="w-[48px] h-[48px]"
            testId="deleted-icon"
          />
        </div>
        <h1 className="text-primary_text text-heading3 text-center pt-6 pb-4 border-b border-[#E5E9EB]">
          {translate("Deleted Successfully")}
        </h1>
        <p className="text-secondary_text text-body1 pt-4" data-testid="delete-success-message">
          {resequencedCount > 0
            ? translate(
                "Bundle plan deleted successfully. All USSD Plan Codes have been reordered accordingly."
              )
            : translate("Bundle plan deleted successfully.")}
        </p>
        <div className="flex justify-end mt-8">
          <Button
            buttonName={translate("Go Back")}
            buttonOnclick={onGoBack}
            buttonWidth="w-fit"
            buttonHight="h-[42px]"
            className="px-5"
            textSize="text-small"
            testId="delete-go-back"
          />
        </div>
      </div>
    </MainModel>
  );
}
